import { Rapportage as RapportageModel } from '@/models/Rapportage';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { Model } from '@/models/Model';
import PieChart from '@/components/PieChart/PieChart.vue';
import BarChart from '@/components/BarChart/BarChart.vue';
import AnimatedNumber from '@/components/AnimatedNumber/AnimatedNumber.vue';
import { PointOptionsObject, SeriesColumnOptions, SeriesOptionsType } from 'highcharts';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import StorageDialog from '@/components/StorageDialog/StorageDialog.vue';

@Component<Rapportage>({
  components: {
    PieChart,
    BarChart,
    AnimatedNumber,
    StorageDialog,
  },
})
export default class Rapportage extends Vue {
  protected $pageTitle = 'Rapportage';

  // Rapportage stats

  protected rapportage: RapportageModel | null = null;

  protected from: string | null = null;

  protected to: string | null = null;

  protected participationStats: PointOptionsObject[] = [];

  protected weeklyParticipationStats: PointOptionsObject[] = [];

  protected municipalityParticipationStats: PointOptionsObject[] = [];

  protected weeklyStatusChangesStats: SeriesOptionsType[] = [];

  protected weeklyStatusChangesLabels: number[] | string[] = [];

  protected avgRuntime = 0;

  protected isDialogOpen = false;

  // Loaders

  protected isLoadingParticipationStats = true;

  protected isLoadingWeeklyParticipationStats = true;

  protected isLoadingMunicipalityParticipationStats = true;

  protected isLoadingWeeklyStatusChanges = true;

  protected isLoadingAvgRuntimeStats = true;

  protected isLoadingRapportage = true;

  protected isDownloadingExport = false;

  protected mounted(): void {
    this.from = new Date(new Date().getFullYear(), 0, 2).toISOString().substr(0, 10);
    this.to = new Date(new Date().getFullYear(), 11, 32).toISOString().substr(0, 10);

    this.initialize();
  }

  protected async initialize(): Promise<void> {
    await this.getParticipationStats();
    await this.getAvgRuntimeStats();
    await this.getWeeklyStatusChanges();
    await this.getMunicipalityParticipationStats();
    await this.getWeeklyParticipationStats();
    await this.getRapportage();
  }

  protected onClickOpenDialog(): void {
    this.isDialogOpen = true;
  }

  protected async getRapportage() : Promise<void> {
    const payload = {};
    if (this.from) {
      Object.assign(payload, { from: this.from });
    }
    if (this.to) {
      Object.assign(payload, { to: this.to });
    }

    this.rapportage = await new RapportageModel()
      .include('review')
      .listRapportage(payload);

    this.isLoadingRapportage = false;
  }

  protected async getParticipationStats() : Promise<void> {
    const data = await new Model()
      .request('get', '/hin/rapportage/hin-participation', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    if (data) {
      this.participationStats = (data as ParticipationDataObject[]).map((dataObject: ParticipationDataObject): PointOptionsObject => ({ name: `${dataObject.name}`, y: dataObject.data }));
    }

    this.isLoadingParticipationStats = false;
  }

  protected async getMunicipalityParticipationStats() : Promise<void> {
    const data = await new Model()
      .request('get', '/hin/rapportage/municipality-participation', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    if (data) {
      this.municipalityParticipationStats = (data as ParticipationDataObject[]).map((dataObject: ParticipationDataObject): PointOptionsObject => ({ name: `${dataObject.name}`, y: dataObject.data }));
    }

    this.isLoadingMunicipalityParticipationStats = false;
  }

  protected async getAvgRuntimeStats() : Promise<void> {
    const data = await new Model()
      .request('get', '/hin/rapportage/average-run-time', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    if (data) {
      this.avgRuntime = data.average_run_time as number;
    }

    this.isLoadingAvgRuntimeStats = false;
  }

  protected async getWeeklyParticipationStats() : Promise<void> {
    const data = await new Model()
      .request('get', '/hin/rapportage/cumulative-hin-reports-per-week', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    if (data) {
      this.weeklyParticipationStats = (data as ParticipationDataObject[]).map((dataObject: ParticipationDataObject): PointOptionsObject => ({ name: `${dataObject.name}`, y: dataObject.data, color: dataObject.is_future ? '#b8def0' : '#67A7C4' }));
    }

    this.isLoadingWeeklyParticipationStats = false;
  }

  protected async getWeeklyStatusChanges() : Promise<void> {
    const data = await new Model()
      .request('get', '/hin/rapportage/stacked-status-changes-per-week', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    if (data) {
      this.weeklyStatusChangesStats = (data.series as columnSeries[]).map((dataObject: columnSeries): SeriesColumnOptions => ({ name: `${dataObject?.name}`, data: dataObject?.data, type: 'column', color: dataObject.color }));
      this.weeklyStatusChangesLabels = data.weeks;
      this.isLoadingWeeklyStatusChanges = false;
    }
  }

  protected get canDownloadExport(): boolean {
    const user = this.$store.getters.user;

    if (user.flamingo || user.has_hin_exports) {
      return true;
    }

    return false;
  }

  protected async downloadRapportageExport(): Promise<void> {
    this.isDownloadingExport = true;
    const response = await new Model()
      .request('get', '/hin/rapportage/excel-export', {}, '')
      .catch((error:AxiosError) => {
        ErrorHandler.network(error);
      });

    const link = document.createElement('a');
    link.href = response.file_url;

    link.setAttribute('download', 'downloadfile');
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(response.file_url);
    link.remove();

    this.isDownloadingExport = false;
  }

  protected get rapportageItems(): Record<string, string | number>[] {
    if (! this.rapportage) {
      return [];
    }
    return [
      {
        status: 'Aantal dossiers in de pijplijn',
        total: 0,
      },
      {
        status: 'Aantal opdrachten',
        total: this.rapportage?.total_reports ? this.rapportage?.total_reports : 0,
      },
      // OUTCOMMENTED ON 2021-04-21, DELETE AFTER 1 MONTH
      // {
      //   status: 'Aantal geplande dossiers',
      //   total: this.rapportage?.total_planned_reports ? this.rapportage?.total_planned_reports : 0,
      // },
      {
        status: 'Aantal dossiers waarvan het besluit bestuur jonger is dan 42 dagen',
        total: this.rapportage?.total_still_liable ? this.rapportage?.total_still_liable : 0,
      },
      {
        status: 'Aantal dossiers in uitvoering',
        total: this.rapportage?.total_in_progress ? this.rapportage?.total_in_progress : 0,
      },
      // OUTCOMMENTED ON 2021-04-21, DELETE AFTER 1 MONTH
      // {
      //   status: 'Aantal dossiers uitgegeven opdrachten',
      //   total: this.rapportage?.total_in_progress ? this.rapportage?.total_in_progress : 0,
      // },
      {
        status: 'Aantal opgeleverde dossiers',
        total: this.rapportage?.total_closed_reports ? this.rapportage?.total_closed_reports : 0,
      },
      {
        status: 'Aantal dossiers waarbij garantietermijn is afgesloten',
        total: this.rapportage?.total_not_liable_anymore ? this.rapportage?.total_not_liable_anymore : 0,
      },
      {
        status: 'Gemiddelde tevredenheid over alle aannemers',
        total: this.rapportage?.total_average_review_contractors ? this.rapportage?.total_average_review_contractors : 0,
      },
      {
        status: 'Gemiddelde tevredenheid over alle procedures',
        total: this.rapportage?.total_average_review_procedures ? this.rapportage?.total_average_review_procedures : 0,
      },
    ];
  }

  protected get rapportageHeaders(): DataTableHeader[] {
    return [
      {
        text: 'Status',
        value: 'status',
        sortable: false,
      },
      {
        text: 'Totaal',
        value: 'total',
        sortable: false,
      },
    ];
  }

  protected get municipalityParticipationLabels(): string[] {
    if (! this.municipalityParticipationStats) {
      return [];
    }
    const labels = this.municipalityParticipationStats.map((dataObject: PointOptionsObject): string => `${dataObject.name}`);
    return labels;
  }

  // protected get closedReportStatsLabels(): string[] {
  //   if (! this.closedReportStats) {
  //     return [];
  //   }
  //   const labels = this.closedReportStats.map((dataObject: PointOptionsObject): string => `Week ${dataObject.name}`);
  //   return labels;
  // }

  // protected get inPreparationReportStatsLabels(): string[] {
  //   if (! this.inPreparationReportStats) {
  //     return [];
  //   }
  //   const labels = this.inPreparationReportStats.map((dataObject: PointOptionsObject): string => `Week ${dataObject.name}`);
  //   return labels;
  // }

  // protected get inRepairReportStatsLabels(): string[] {
  //   if (! this.inRepairReportStats) {
  //     return [];
  //   }
  //   const labels = this.inRepairReportStats.map((dataObject: PointOptionsObject): string => `Week ${dataObject.name}`);
  //   return labels;
  // }

  protected get weeklyParticipationLabels(): string[] {
    if (! this.weeklyParticipationStats) {
      return [];
    }
    const labels = this.weeklyParticipationStats.map((dataObject: PointOptionsObject): string => `Week ${dataObject.name}`);
    return labels;
  }

  @Watch('from')
  public fromChanged(): void {
    this.getRapportage();
  }

  @Watch('to')
  public toChanged(): void {
    this.getRapportage();
  }
}

type ParticipationDataObject = {
  name?: string,
  data?: number,
  is_future?: boolean,
}

type StatusChangeObject = {
  name?: string,
  data?: number,
}

type weeklyStatusChangeObject = {
  weeks: string[] | number[],
  series?: columnSeries[],
}

type columnSeries = {
  name?: string,
  data?: number[],
  color?: string,
}
